import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BRAND = '#1A3C34';
const ACCENT = [42, 157, 143]; // #2A9D8F

const todayStr = () => new Date().toLocaleDateString('sq-AL');

// ---------- PDF: structured financial report ----------
// sections: [{ heading: string, rows: [[label, value], ...] }]
export function exportReportPDF({ title, subtitle, sections, filename }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Brand header
  doc.setFontSize(18);
  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.text('FinanciAL', 40, 46);
  doc.setFontSize(13);
  doc.setTextColor(60);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 40, 68);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(subtitle, 40, 84);
  }
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(`Gjeneruar: ${todayStr()}`, pageWidth - 40, 46, { align: 'right' });

  let startY = 100;
  sections.forEach((section) => {
    autoTable(doc, {
      startY,
      head: [[section.heading, '']],
      body: section.rows,
      theme: 'striped',
      headStyles: { fillColor: ACCENT, textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 5 },
      margin: { left: 40, right: 40 },
    });
    startY = doc.lastAutoTable.finalY + 16;
  });

  doc.save(filename || 'raport.pdf');
}

// ---------- PDF: AI analysis (long Albanian text) ----------
export function exportAnalysisPDF({ title, dateStr, text, filename }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const maxWidth = pageWidth - marginX * 2;

  doc.setFontSize(18);
  doc.setTextColor(BRAND);
  doc.setFont('helvetica', 'bold');
  doc.text('FinanciAL', marginX, 46);
  doc.setFontSize(13);
  doc.setTextColor(60);
  doc.text(title || 'Analiza AI e Dokumenteve', marginX, 68);
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gjeneruar: ${dateStr || todayStr()}`, pageWidth - marginX, 46, { align: 'right' });

  let y = 96;
  const lineHeight = 14;

  // Light markdown cleanup -> render as paragraphs
  const cleaned = (text || '')
    .replace(/\*\*/g, '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/`/g, '');

  const paragraphs = cleaned.split('\n');
  paragraphs.forEach((para) => {
    const trimmed = para.trim();
    if (trimmed === '') {
      y += lineHeight / 2;
      return;
    }
    const isHeading = /^[0-9]+\.\s|^[A-ZËÇ][^a-zëç]{0,40}$/.test(trimmed) && trimmed.length < 60;
    doc.setFont('helvetica', isHeading ? 'bold' : 'normal');
    doc.setFontSize(isHeading ? 11 : 9.5);
    doc.setTextColor(isHeading ? BRAND : 50);
    const wrapped = doc.splitTextToSize(trimmed, maxWidth);
    wrapped.forEach((line) => {
      if (y > pageHeight - 50) {
        doc.addPage();
        y = 50;
      }
      doc.text(line, marginX, y);
      y += lineHeight;
    });
  });

  doc.save(filename || 'analiza-ai.pdf');
}

// ---------- Excel ----------
// sheets: [{ name: string, aoa: [[...], ...] }]
export function exportExcel({ sheets, filename }) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    XLSX.utils.book_append_sheet(wb, ws, (sheet.name || 'Sheet').slice(0, 31));
  });
  XLSX.writeFile(wb, filename || 'raport.xlsx');
}

// ---------- CSV ----------
// rows: [[...], ...]
export function exportCSV({ rows, filename }) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell == null ? '' : String(cell);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(',')
    )
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'raport.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
